// --- START EDIT: fetch CSRF token for split-domain; attach for unsafe methods ---
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

let csrfTokenCache: string | null = null;

// Read Vite environment (typed via src/vite-env.d.ts)
const env: ImportMetaEnv | undefined =
  typeof import.meta !== "undefined" ? import.meta.env : undefined;

/**
 * Compute API base:
 * - Prefer VITE_API_BASE_URL (e.g., https://api.dm-3dprinting.com)
 * - Fall back to current origin (for proxied dev)
 * - Finally to localhost (for local dev)
 */
const apiBaseFromEnv = env?.VITE_API_BASE_URL?.trim();
const apiBaseFromWindow =
  typeof window !== "undefined" ? window.location.origin : undefined;

const API_BASE =
  apiBaseFromEnv || apiBaseFromWindow || "http://localhost:8000";

const BASE_URL = `${API_BASE.replace(/\/$/, "")}/api`;

/**
 * Get CSRF token from cookie (same-site only).
 * For split-domain, also try the cached token populated by GET /api/csrf-token.
 */
function getCsrfToken(): string | null {
  const match =
    typeof document !== "undefined"
      ? document.cookie.match(/csrf_token=([^;]+)/)
      : null;
  if (match) return match[1];
  return csrfTokenCache;
}

async function ensureCsrfToken(): Promise<void> {
  if (getCsrfToken()) return;
  try {
    const res = await axios.get(`${BASE_URL}/csrf-token`, { withCredentials: true });
    if (res.data?.csrf_token) {
      csrfTokenCache = res.data.csrf_token;
    }
  } catch {
    // Ignore; backend may still accept requests if cookie is readable
  }
}

// Request config with custom retry flags to avoid infinite loops
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _csrfRetry?: boolean;
}

// Auth and routing state (shared across instances)
let isRefreshing = false;
let isLoggingOut = false;
let failedQueue: Array<() => void> = [];

// Protected path helper (unchanged)
const protectedPath = ["/dashboard", "/profile", "/settings", "/inventory", "/admin", "/account"];
function isOnProtectedPath() {
  return protectedPath.some((path) => window.location.pathname.startsWith(path));
}

export function setIsLoggingOut(value: boolean) {
  isLoggingOut = value;
}

/**
 * Seed the CSRF token cache from an external source (e.g. the login response
 * body). This keeps the cache in sync with the cookie that the backend just
 * set, avoiding a stale-token mismatch on the very first mutation after login.
 */
export function setCsrfToken(token: string): void {
  csrfTokenCache = token;
}

export const AUTH_EVENTS = {
  REFRESHED: "auth:refreshed",
  LOGGED_IN: "auth:loggedIn",
  LOGGED_OUT: "auth:loggedOut",
} as const;

// --- START EDIT: Configurable timeouts and shared interceptor attachment ---
function parseTimeout(value: unknown, fallback: number, min = 1000, max = 300000): number {
  const n = typeof value === "string" ? parseInt(value, 10) : NaN;
  if (!isFinite(n) || n <= 0) return fallback;
  return Math.min(Math.max(n, min), max);
}

const TIMEOUT_SHORT = parseTimeout(env?.VITE_API_TIMEOUT_SHORT, 15000);
const TIMEOUT_LONG = parseTimeout(env?.VITE_API_TIMEOUT_LONG, 210000); // 3 minutes default

/**
 * Create an axios instance with base settings.
 */
function createAxiosInstance(timeoutMs: number): AxiosInstance {
  return axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    timeout: timeoutMs,
    headers: {
      Accept: "application/json",
    },
  });
}

/**
 * Attach CSRF and refresh-token interceptors to a given instance.
 */
function attachInterceptors(instance: AxiosInstance) {
  // Add CSRF token for unsafe methods
  instance.interceptors.request.use(
    async (config) => {
      const method = (config.method || "get").toLowerCase();
      if (["post", "put", "patch", "delete"].includes(method)) {
        await ensureCsrfToken();
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          config.headers = config.headers || {};
          config.headers["X-CSRF-Token"] = csrfToken;
          if (!config.headers["Content-Type"]) {
            config.headers["Content-Type"] = "application/json";
          }
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  const processQueue = () => {
    failedQueue.forEach((cb) => cb());
    failedQueue = [];
  };

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = (error.config ?? {}) as RetriableConfig;

      if (isLoggingOut) {
        return Promise.reject(error);
      }

      // ── CSRF mismatch recovery ────────────────────────────────────────────
      // When the backend rejects a mutation with 403 "CSRF token validation
      // failed", the cached token is stale (e.g. a new token was issued at
      // login but the cache was never updated).  Clear the cache so that
      // ensureCsrfToken() is forced to fetch a fresh token, then replay the
      // original request exactly once.
      if (
        error.response?.status === 403 &&
        !originalRequest._csrfRetry &&
        (error.response?.data as { detail?: string })?.detail ===
          "CSRF token validation failed"
      ) {
        originalRequest._csrfRetry = true;
        csrfTokenCache = null;
        // The request interceptor will call ensureCsrfToken() on retry,
        // fetching a fresh token that matches the current cookie.
        return instance(originalRequest);
      }
      // ─────────────────────────────────────────────────────────────────────

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !(originalRequest.url || "").includes("/refresh-token")
      ) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            await instance.post("/refresh-token", {}, { withCredentials: true });
            isRefreshing = false;
            processQueue();

            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent(AUTH_EVENTS.REFRESHED));
            }

            return instance(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;
            failedQueue = [];
            if (isOnProtectedPath() && typeof window !== "undefined") {
              window.location.href = "/login";
            }
            return Promise.reject(refreshError);
          }
        } else {
          return new Promise((resolve, reject) => {
            failedQueue.push(async () => {
              try {
                resolve(await instance(originalRequest));
              } catch (err) {
                reject(err);
              }
            });
          });
        }
      }

      return Promise.reject(error);
    }
  );
}
// --- END EDIT: Configurable timeouts and shared interceptor attachment ---

// --- START EDIT: Export short and long axios instances ---
const axiosShort = createAxiosInstance(TIMEOUT_SHORT);
attachInterceptors(axiosShort);

const axiosLong = createAxiosInstance(TIMEOUT_LONG);
attachInterceptors(axiosLong);

// Keep default export name for backward compatibility (short-timeout)
const axiosInstance = axiosShort;

export { axiosLong };
export default axiosInstance;
// --- END EDIT: Export short and long axios instances ---
// --- END EDIT: fetch CSRF token for split-domain; attach for unsafe methods ---